import { deserializeBoardTransform } from './BoardTransform';
import { preloadImageAsync } from './ImageLoader';
import { getType } from './ItemType';

export function getCurrentPage(instance) {
    const pageId = instance.currentPageId || '0';
    return instance.pages?.find(p => p.id === pageId);
}

function forEachAttached(instance, action) {
    const attachedInstances = getCurrentPage(instance)?.instances;
    if (attachedInstances) {
        for (let i = 0; i < attachedInstances.length; i++) {
            action(attachedInstances[i]);
        }
    }
}

async function forEachAttachedAsync(instance, asyncAction) {
    const attachedInstances = getCurrentPage(instance)?.instances;

    if (attachedInstances) {
        for (let i = 0; i < attachedInstances.length; i++) {
            await asyncAction(attachedInstances[i]);
        }
    }
}

export async function preloadThumbnailAsync(instance) {
    try {
        function onLoad(e) {
            instance.thumbnailLoaded = true;
            instance.thumbnailSize = {
                width: e.srcElement.width,
                height: e.srcElement.height
            }
        };

        await preloadImageAsync(instance.thumbnailUrl, onLoad);
        //await forEachAttachedAsync(instance, preloadAttachedThumbnailsAsync);

        return instance;
    }
    catch (error) {
        console.log(`${instance.id} : failed thumbnail initialization`);
        return undefined;
    }
}

async function preloadAttachedThumbnailsAsync(instance) {
    await preloadImageAsync(instance.thumbnailUrl, () => instance.thumbnailLoaded = true);
    await forEachAttachedAsync(instance, preloadAttachedThumbnailsAsync);
}

function getExtensionByTypeId(typeId) {
    switch (typeId) {
        case "Plop.UI.Items.Note.NotePlugin":
            return "png";
        case "Plop.BasePlugin.Image.ImagePlugin":
        case "Plop.UI.Items.CorkBoard.CorkBoardPlugin":
        case "Plop.UI.Items.Blanksheet.BlanksheetPlugin":
        case "Plop.HelixPlugin3D.PanoramaPlugin":
            return "jpg";
        case "Plop.BasePlugin.Slideshow.SlideshowFilePlugin":
        case "Plop.BasePlugin.ProductSheet.ProductSheetPlugin":
            return "pdf";
        default:
            return undefined;
    }
}

export function initializeInstance(i, isRoot = true) {
    let noQuerryUrl = undefined;
    let extension = undefined;

    if (i.publicWebUrl !== undefined) {
        noQuerryUrl = i.publicWebUrl?.split('?')[0];
        extension = noQuerryUrl?.split('.').slice(-1)[0]?.toLowerCase();
    } else if (i.fileName != undefined) {
        noQuerryUrl = i.fileName;
        extension = noQuerryUrl?.split('.').slice(-1)[0]?.toLowerCase();
    } else {
        extension = getExtensionByTypeId(i.referenceData.typeId);
    }

    i.itemType = getType(extension);
    i.transform = deserializeBoardTransform(i.position);
    forEachAttached(i, a => initializeInstance(a, false));
    // we need to have all attached initialized and the parent's transform set for the overflow calculation
    i.childrenOverflow = computeChildrenOverflow(i, isRoot);
};


// Warning : margins are computed only for current page as it's the only one used for now
function computeChildrenOverflow(instance, isRoot) {
    const currentPage = getCurrentPage(instance);
    const overflows = currentPage?.instances?.map(i => {
        const x = i.transform.x;
        const y = i.transform.y;
        let R = 1;
        if (instance.transform)
            R = instance.transform.width / instance.transform.height;
        else if (instance.thumbnailSize)
            R = instance.thumbnailSize.width / instance.thumbnailSize.height;
        const r = i.transform.width / i.transform.height;
        const d = i.transform.relativeWidthToParent; // d = w / W

        // All margins percentages (vertical & horizontal) are relative to parent's ** WIDTH **
        let overflow = {
            top: (d / (2 * r)) - y / R,             // (h/2 - y * H) / W
            bottom: (d / (2 * r)) + (y - 1) / R,    // (y * H + h/2 - H) / W
            left: d / 2 - x,                        // (w/2 - x * W) / W
            right: x + d / 2 - 1,                   // (x * W + w/2 - W) / W
        };

        if (i.childrenOverflow) {
            const c = i.childrenOverflow;
            overflow = {
                top: overflow.top + (c.top * d),
                bottom: overflow.bottom + (c.bottom * d),
                left: overflow.left + (c.left * d),
                right: overflow.right + (c.right * d)
            };
        }

        return overflow;
    });

    if (overflows) {
        let maxOverflows = {
            top: Math.max(0, ...overflows.map(o => o.top)),
            right: Math.max(0, ...overflows.map(o => o.right)),
            bottom: Math.max(0, ...overflows.map(o => o.bottom)),
            left: Math.max(0, ...overflows.map(o => o.left)),
        };


        if (isRoot) {
            // parent's width is changed by those margins
            // => newMargin = oldMargin/(100% + sideMargins)

            const newWidth = 1 + maxOverflows.left + maxOverflows.right;

            return {
                top: maxOverflows.top / newWidth,
                right: maxOverflows.right / newWidth,
                bottom: maxOverflows.bottom / newWidth,
                left: maxOverflows.left / newWidth,
            }
        } else {
            return maxOverflows;
        }
    }

    return undefined;
}